import { useCallback, useEffect, useState } from "react";
import { Plus, Loader2, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { MonthFilter, PageHeader } from "../../components/Common";
import { ReviewGallery } from "../../components/ReviewGallery";
import { DriverSelect } from "./AdminPayments";
import { api, cleanParams, currentMonth, formatApiError, todayStr } from "../../lib/api";

function UploadDialog({ open, onOpenChange, onSaved, defaultDriverId }) {
  const [form, setForm] = useState({ driver_id: "all", tour_date: todayStr(), tour_name: "", note: "" });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setForm({ driver_id: defaultDriverId, tour_date: todayStr(), tour_name: "", note: "" }); setFile(null); } }, [open, defaultDriverId]);
  useEffect(() => {
    if (!file) return setPreview(null);
    const u = URL.createObjectURL(file);
    setPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.driver_id === "all") return toast.error("Pilih driver");
    if (!file) return toast.error("Pilih screenshot review");
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", file);
    setSaving(true);
    try {
      await api.post("/reviews", fd);
      toast.success("Review tamu tersimpan");
      onOpenChange(false);
      onSaved(form.tour_date.slice(0, 7));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="review-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Upload Review Tamu</DialogTitle>
          <DialogDescription>Screenshot review akan terlihat di akun driver terkait.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3 items-end">
            <DriverSelect value={form.driver_id} onChange={(v) => setForm((f) => ({ ...f, driver_id: v }))} testId="review-driver-select" />
            <div className="space-y-1.5"><Label>Tanggal Tour</Label><Input type="date" required value={form.tour_date} onChange={set("tour_date")} data-testid="review-date-input" /></div>
          </div>
          <div className="space-y-1.5"><Label>Nama Tour (opsional)</Label><Input value={form.tour_name} onChange={set("tour_name")} data-testid="review-tour-input" /></div>
          <div className="space-y-1.5"><Label>Catatan (opsional)</Label><Input value={form.note} onChange={set("note")} placeholder="Contoh: TripAdvisor, Google Review" data-testid="review-note-input" /></div>
          <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 hover:border-emerald-500 bg-slate-50 p-6 cursor-pointer transition-colors text-center">
            {preview ? <img src={preview} alt="preview" className="max-h-56 rounded-lg object-contain" /> : <UploadCloud className="h-8 w-8 text-slate-400" />}
            <span className="text-sm font-medium text-slate-700" data-testid="review-file-name">{file ? file.name : "Pilih screenshot review (JPG/PNG/WEBP)"}</span>
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="review-file-input" />
          </label>
          <Button type="submit" disabled={saving} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 font-semibold" data-testid="review-submit-btn">{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Simpan Review</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminReviews() {
  const [month, setMonth] = useState(currentMonth());
  const [driverId, setDriverId] = useState("all");
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => api.get("/reviews", { params: cleanParams({ month, driver_id: driverId }) }).then((r) => setReviews(r.data)), [month, driverId]);
  useEffect(() => { load(); }, [load]);

  const remove = async (r) => {
    try { await api.delete(`/reviews/${r.id}`); toast.success("Review dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Tamu" title="Review Tamu">
        <MonthFilter value={month} onChange={setMonth} />
        <DriverSelect value={driverId} onChange={setDriverId} />
        <Button onClick={() => setOpen(true)} className="h-10 bg-emerald-600 hover:bg-emerald-700 font-semibold" data-testid="add-review-btn"><Plus className="h-4 w-4 mr-1" />Upload Review</Button>
      </PageHeader>
      <ReviewGallery reviews={reviews} showDriver onDelete={remove} />
      <UploadDialog open={open} onOpenChange={setOpen} defaultDriverId={driverId} onSaved={(m) => (month && m !== month ? setMonth(m) : load())} />
    </div>
  );
}
